package com.dotpx.ambient

import kotlin.math.max
import kotlin.math.min

private const val RANDOM_EXACT_PRESET_MODULUS = 20uL

internal class SceneSoundProfile {
    var texture = TextureKind.Room
        private set
    var bedLevel = 0f
        private set
    var brightness = 0f
        private set
    var motionRate = 0.0
        private set
    var droneFrequency = 0.0
        private set
    var droneLevel = 0f
        private set
    var toneFrequency = 0.0
        private set
    var toneLevel = 0f
        private set
    var tonePulseRate = 0.0
        private set
    var event = EventKind.None
        private set
    var eventLevel = 0f
        private set
    var eventDelayLow = 1.0
        private set
    var eventDelayHigh = 1.0
        private set
    var secondaryEvent = EventKind.None
        private set
    var secondaryLevel = 0f
        private set
    var secondaryDelayLow = 1.0
        private set
    var secondaryDelayHigh = 1.0
        private set
    var matchesPresetCombination = true
        private set

    private val random = SplitMix64(0uL)
    private var selectedEvent = EventKind.None
    private var selectedLevel = 0f
    private var selectedDelayLow = 1.0
    private var selectedDelayHigh = 1.0

    fun reconfigure(
        scene: AmbienceScene,
        seed: ULong,
        usePresetCombination: Boolean = false,
        materialCycle: SoundMaterialCycle? = null,
    ) {
        random.reset(seed xor 0xD1B5_4A32_D192_ED03uL)
        if (materialCycle == null) {
            reconfigureUnconstrained(scene, seed, usePresetCombination)
        } else {
            reconfigureConstrained(scene, seed, usePresetCombination, materialCycle)
        }
    }

    private fun reconfigureUnconstrained(
        scene: AmbienceScene,
        seed: ULong,
        usePresetCombination: Boolean,
    ) {
        val exactPreset = usePresetCombination || seed % RANDOM_EXACT_PRESET_MODULUS == 0uL
        var textureScene = if (exactPreset) scene else biasedScene(scene, 0.82)
        val bedScene = if (exactPreset) scene else biasedScene(scene, 0.80)
        val droneScene = if (exactPreset) scene else biasedScene(scene, 0.76)
        val toneScene = if (exactPreset) scene else biasedScene(scene, 0.74)
        if (!exactPreset && textureScene == scene && bedScene == scene &&
            droneScene == scene && toneScene == scene
        ) {
            val preferredTexture = scenePreset(scene).texture
            textureScene = AmbienceScene.entries.first { scenePreset(it).texture != preferredTexture }
        }
        val preferred = scenePreset(scene)
        if (exactPreset) {
            selectExactEvent(
                preferred.event,
                preferred.eventLevel,
                preferred.eventDelayLow,
                preferred.eventDelayHigh,
            )
        } else {
            selectEvent(
                preferred.event,
                preferred.eventLevel,
                preferred.eventDelayLow,
                preferred.eventDelayHigh,
                0.78,
            )
        }
        val primaryKind = selectedEvent
        val primaryLevel = selectedLevel
        val primaryDelayLow = selectedDelayLow
        val primaryDelayHigh = selectedDelayHigh
        if (exactPreset) {
            selectExactEvent(
                preferred.secondaryEvent,
                preferred.secondaryLevel,
                preferred.secondaryDelayLow,
                preferred.secondaryDelayHigh,
            )
        } else {
            selectEvent(
                preferred.secondaryEvent,
                preferred.secondaryLevel,
                preferred.secondaryDelayLow,
                preferred.secondaryDelayHigh,
                0.72,
            )
        }
        publishResolved(
            scene = scene,
            textureScene = textureScene,
            bedScene = bedScene,
            droneScene = droneScene,
            toneScene = toneScene,
            resolvedTexture = scenePreset(textureScene).texture,
            preferred = preferred,
            primaryKind = primaryKind,
            primaryLevel = primaryLevel,
            primaryDelayLow = primaryDelayLow,
            primaryDelayHigh = primaryDelayHigh,
            secondaryKind = selectedEvent,
            secondaryLevel = selectedLevel,
            secondaryDelayLow = selectedDelayLow,
            secondaryDelayHigh = selectedDelayHigh,
        )
    }

    private fun reconfigureConstrained(
        scene: AmbienceScene,
        seed: ULong,
        usePresetCombination: Boolean,
        materialCycle: SoundMaterialCycle,
    ) {
        val preferred = scenePreset(scene)
        materialCycle.modify {
            val exactPreset = usePresetCombination ||
                (seed % RANDOM_EXACT_PRESET_MODULUS == 0uL &&
                    canTakeExact(preferred.texture, preferred.event, preferred.secondaryEvent))
            val textureScene: AmbienceScene
            val bedScene: AmbienceScene
            val droneScene: AmbienceScene
            val toneScene: AmbienceScene
            val resolvedTexture: TextureKind
            val primaryKind: EventKind
            val primaryLevel: Float
            val primaryDelayLow: Double
            val primaryDelayHigh: Double
            val secondaryKind: EventKind
            val secondaryLevel: Float
            val secondaryDelayLow: Double
            val secondaryDelayHigh: Double
            if (exactPreset) {
                textureScene = scene
                bedScene = scene
                droneScene = scene
                toneScene = scene
                resolvedTexture = preferred.texture
                primaryKind = preferred.event
                primaryLevel = preferred.eventLevel
                primaryDelayLow = preferred.eventDelayLow
                primaryDelayHigh = preferred.eventDelayHigh
                secondaryKind = preferred.secondaryEvent
                secondaryLevel = preferred.secondaryLevel
                secondaryDelayLow = preferred.secondaryDelayLow
                secondaryDelayHigh = preferred.secondaryDelayHigh
            } else {
                bedScene = biasedScene(scene, 0.80)
                droneScene = biasedScene(scene, 0.76)
                toneScene = biasedScene(scene, 0.74)
                var chosenTexture = chooseTexture(preferred.texture, availableTextures())
                var chosenTextureScene =
                    if (chosenTexture == preferred.texture) scene else sceneWithTexture(chosenTexture, scene)
                if (chosenTextureScene == scene && bedScene == scene &&
                    droneScene == scene && toneScene == scene &&
                    chosenTexture == preferred.texture
                ) {
                    val alternatives = availableTextures().filter { it != preferred.texture }
                    if (alternatives.isNotEmpty()) {
                        chosenTexture = pickUniform(alternatives)
                        chosenTextureScene = sceneWithTexture(chosenTexture, scene)
                    }
                }
                textureScene = chosenTextureScene
                resolvedTexture = chosenTexture
                val primary = chooseEvent(
                    preferred.event,
                    preferred.eventLevel,
                    preferred.eventDelayLow,
                    preferred.eventDelayHigh,
                    0.78,
                    availableEvents(),
                    preferred,
                )
                primaryKind = primary.kind
                primaryLevel = primary.level
                primaryDelayLow = primary.delayLow
                primaryDelayHigh = primary.delayHigh
                val secondary = chooseEvent(
                    preferred.secondaryEvent,
                    preferred.secondaryLevel,
                    preferred.secondaryDelayLow,
                    preferred.secondaryDelayHigh,
                    0.72,
                    availableEvents(excluding = primaryKind),
                    preferred,
                )
                secondaryKind = secondary.kind
                secondaryLevel = secondary.level
                secondaryDelayLow = secondary.delayLow
                secondaryDelayHigh = secondary.delayHigh
            }
            publishResolved(
                scene = scene,
                textureScene = textureScene,
                bedScene = bedScene,
                droneScene = droneScene,
                toneScene = toneScene,
                resolvedTexture = resolvedTexture,
                preferred = preferred,
                primaryKind = primaryKind,
                primaryLevel = primaryLevel,
                primaryDelayLow = primaryDelayLow,
                primaryDelayHigh = primaryDelayHigh,
                secondaryKind = secondaryKind,
                secondaryLevel = secondaryLevel,
                secondaryDelayLow = secondaryDelayLow,
                secondaryDelayHigh = secondaryDelayHigh,
            )
            consume(resolvedTexture, primaryKind, secondaryKind)
        }
    }

    private fun publishResolved(
        scene: AmbienceScene,
        textureScene: AmbienceScene,
        bedScene: AmbienceScene,
        droneScene: AmbienceScene,
        toneScene: AmbienceScene,
        resolvedTexture: TextureKind,
        preferred: ScenePreset,
        primaryKind: EventKind,
        primaryLevel: Float,
        primaryDelayLow: Double,
        primaryDelayHigh: Double,
        secondaryKind: EventKind,
        secondaryLevel: Float,
        secondaryDelayLow: Double,
        secondaryDelayHigh: Double,
    ) {
        val bedProfile = scenePreset(bedScene)
        val droneProfile = scenePreset(droneScene)
        val toneProfile = scenePreset(toneScene)
        val duplicateEvents = primaryKind == secondaryKind && primaryKind != EventKind.None
        val duplicateLevelScale = if (duplicateEvents) 0.72f else 1f
        val duplicateDelayScale = if (duplicateEvents) 1.4 else 1.0
        val eventsMatchPreset =
            (primaryKind == preferred.event && secondaryKind == preferred.secondaryEvent) ||
                (primaryKind == preferred.secondaryEvent && secondaryKind == preferred.event)

        texture = resolvedTexture
        bedLevel = varied(bedProfile.bedLevel, 0.13f, 0.035f, 0.18f)
        brightness = varied(bedProfile.brightness, 0.16f, 0.08f, 0.92f)
        motionRate = varied(bedProfile.motionRate, 0.20, 0.006, 1.8)
        droneFrequency = variedOptional(droneProfile.droneFrequency, 0.10, 24.0, 220.0)
        droneLevel = variedOptional(droneProfile.droneLevel, 0.18f, 0.002f, 0.034f)
        toneFrequency = variedOptional(toneProfile.toneFrequency, 0.045, 20.0, 7_200.0)
        toneLevel = variedOptional(toneProfile.toneLevel, 0.18f, 0.001f, 0.016f)
        tonePulseRate = variedOptional(toneProfile.tonePulseRate, 0.15, 0.035, 15.0)
        event = primaryKind
        eventLevel = variedOptional(primaryLevel, 0.16f, 0.012f, 0.080f) * duplicateLevelScale
        varyRange(primaryDelayLow, primaryDelayHigh, 0.18, duplicateDelayScale)
        eventDelayLow = selectedDelayLow
        eventDelayHigh = selectedDelayHigh
        this.secondaryEvent = secondaryKind
        this.secondaryLevel = variedOptional(secondaryLevel, 0.16f, 0.012f, 0.080f) * duplicateLevelScale
        varyRange(secondaryDelayLow, secondaryDelayHigh, 0.18, duplicateDelayScale)
        this.secondaryDelayLow = selectedDelayLow
        this.secondaryDelayHigh = selectedDelayHigh

        val estimatedPeak = bedLevel * 1.6f + droneLevel * 1.28f + toneLevel +
            (eventLevel + this.secondaryLevel) * 1.28f
        val levelScale = min(1f, 0.42f / max(estimatedPeak, 0.001f))
        bedLevel *= levelScale
        droneLevel *= levelScale
        toneLevel *= levelScale
        eventLevel *= levelScale
        this.secondaryLevel *= levelScale
        matchesPresetCombination = textureScene == scene && bedScene == scene &&
            droneScene == scene && toneScene == scene && eventsMatchPreset
    }

    private fun biasedScene(preferred: AmbienceScene, retention: Double): AmbienceScene {
        if (random.nextUnitDouble() < retention) return preferred
        return AmbienceScene.entries[(random.nextULong() % SCENE_PRESETS.size.toULong()).toInt()]
    }

    private fun chooseTexture(preferred: TextureKind, remaining: List<TextureKind>): TextureKind {
        if (remaining.isEmpty()) return preferred
        if (preferred in remaining && random.nextUnitDouble() < 0.82) return preferred
        return pickUniform(remaining)
    }

    private fun chooseEvent(
        preferred: EventKind,
        level: Float,
        delayLow: Double,
        delayHigh: Double,
        retention: Double,
        remaining: List<EventKind>,
        scenePreset: ScenePreset,
    ): ChosenEvent {
        if (random.nextUnitDouble() < 0.05 || remaining.isEmpty()) {
            return ChosenEvent(EventKind.None, 0f, 8.0, 24.0)
        }
        val kind = if (preferred != EventKind.None && preferred in remaining &&
            random.nextUnitDouble() < retention
        ) {
            preferred
        } else {
            pickUniform(remaining)
        }
        return parametersForEvent(kind, preferred, level, delayLow, delayHigh, scenePreset)
    }

    private fun parametersForEvent(
        kind: EventKind,
        preferred: EventKind,
        level: Float,
        delayLow: Double,
        delayHigh: Double,
        scenePreset: ScenePreset,
    ): ChosenEvent = when {
        kind == EventKind.None -> ChosenEvent(EventKind.None, 0f, 8.0, 24.0)
        kind == preferred -> ChosenEvent(kind, level, delayLow, delayHigh)
        kind == scenePreset.event -> ChosenEvent(
            kind,
            scenePreset.eventLevel,
            scenePreset.eventDelayLow,
            scenePreset.eventDelayHigh,
        )
        kind == scenePreset.secondaryEvent -> ChosenEvent(
            kind,
            scenePreset.secondaryLevel,
            scenePreset.secondaryDelayLow,
            scenePreset.secondaryDelayHigh,
        )
        else -> catalogEvent(kind)
    }

    private fun catalogEvent(kind: EventKind): ChosenEvent {
        SCENE_PRESETS.forEach { preset ->
            if (preset.event == kind) {
                return ChosenEvent(kind, preset.eventLevel, preset.eventDelayLow, preset.eventDelayHigh)
            }
            if (preset.secondaryEvent == kind) {
                return ChosenEvent(
                    kind,
                    preset.secondaryLevel,
                    preset.secondaryDelayLow,
                    preset.secondaryDelayHigh,
                )
            }
        }
        return ChosenEvent(kind, 0.03f, 8.0, 24.0)
    }

    private fun sceneWithTexture(kind: TextureKind, excluding: AmbienceScene): AmbienceScene =
        AmbienceScene.entries.firstOrNull { it != excluding && scenePreset(it).texture == kind }
            ?: AmbienceScene.entries.first { scenePreset(it).texture == kind }

    private fun <T> pickUniform(items: List<T>): T =
        items[(random.nextULong() % items.size.toULong()).toInt()]

    private data class ChosenEvent(
        val kind: EventKind,
        val level: Float,
        val delayLow: Double,
        val delayHigh: Double,
    )

    private fun selectEvent(
        preferred: EventKind,
        level: Float,
        delayLow: Double,
        delayHigh: Double,
        retention: Double,
    ) {
        if (random.nextUnitDouble() < 0.05) {
            selectedEvent = EventKind.None
            selectedLevel = 0f
            selectedDelayLow = 8.0
            selectedDelayHigh = 24.0
            return
        }
        if (random.nextUnitDouble() < retention) {
            selectedEvent = preferred
            selectedLevel = level
            selectedDelayLow = delayLow
            selectedDelayHigh = delayHigh
            return
        }
        val source = SCENE_PRESETS[(random.nextULong() % SCENE_PRESETS.size.toULong()).toInt()]
        if (random.nextULong() and 1uL == 0uL) {
            selectedEvent = source.event
            selectedLevel = source.eventLevel
            selectedDelayLow = source.eventDelayLow
            selectedDelayHigh = source.eventDelayHigh
        } else {
            selectedEvent = source.secondaryEvent
            selectedLevel = source.secondaryLevel
            selectedDelayLow = source.secondaryDelayLow
            selectedDelayHigh = source.secondaryDelayHigh
        }
    }

    private fun selectExactEvent(
        event: EventKind,
        level: Float,
        delayLow: Double,
        delayHigh: Double,
    ) {
        selectedEvent = event
        selectedLevel = level
        selectedDelayLow = delayLow
        selectedDelayHigh = delayHigh
    }

    private fun varied(value: Float, spread: Float, low: Float, high: Float): Float =
        (value * (1f + random.nextBipolarFloat() * spread)).coerceIn(low, high)

    private fun variedOptional(value: Float, spread: Float, low: Float, high: Float): Float =
        if (value > 0f) varied(value, spread, low, high) else 0f

    private fun varied(value: Double, spread: Double, low: Double, high: Double): Double =
        (value * (1.0 + (random.nextUnitDouble() * 2.0 - 1.0) * spread)).coerceIn(low, high)

    private fun variedOptional(value: Double, spread: Double, low: Double, high: Double): Double =
        if (value > 0.0) varied(value, spread, low, high) else 0.0

    private fun varyRange(low: Double, high: Double, spread: Double, scale: Double) {
        val combinedScale = (1.0 + (random.nextUnitDouble() * 2.0 - 1.0) * spread) * scale
        selectedDelayLow = max(low * combinedScale, 0.05)
        selectedDelayHigh = max(high * combinedScale, 0.1)
    }

}
